import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { GeneralEnquiryBoard } from '@api/BoardSDK';
import { db } from '@vibe/db/server';
import { submissions } from '@generated/models';
import { getCurrentUser } from '@api/current-user.server';
import { verifyStaffMember } from '@generated/server/staff.server';
// getServerApiClient kept for potential fallback GraphQL calls

const enquirySchema = z.object({
  // Staff identity (from monday sign-in context) — re-verified server-side
  staffUserId: z.string().optional(),
  staffAccountId: z.string().optional(),
  // Contact info
  fullName: z.string().optional(),
  name: z.string().optional(),
  email: z.string().email(),
  contactNumber: z.string(),
  suburb: z.object({
    address: z.string(),
    lat: z.number(),
    lng: z.number(),
    city: z.string().optional(),
    country: z.string().optional()
  }).nullable().optional(),
  state: z.string().optional(),
  
  // Enquiry type
  enquiryType: z.enum(['Event Enquiry', 'General Enquiry']),
  
  // Event-specific
  eventName: z.string().optional(),
  specificEnquiryType: z.string().optional(),
  pleaseSpecify: z.string().optional(),
  weddingType: z.string().optional(),
  venuePreferences: z.array(z.string()).optional(),
  venueIfOther: z.string().optional(),
  subVenuePreferences: z.array(z.string()).optional(),
  socialEventType: z.string().optional(),
  corporateEventType: z.string().optional(),
  businessName: z.string().optional(),
  industry: z.string().optional(),
  companyWebsite: z.string().optional(),
  externalCateringEventType: z.string().optional(),
  externalVenue: z.string().optional(),
  
  // Date selection
  eventDateType: z.string().optional(),
  eventDate: z.string().nullable().optional(),
  eventDateRange: z.object({
    start: z.string().nullable(),
    end: z.string().nullable()
  }).optional(),
  isFlexibleWithDates: z.string().optional(),
  flexibleDates: z.array(z.string()).optional(),
  flexibleDateRange: z.object({
    start: z.string().nullable(),
    end: z.string().nullable()
  }).optional(),
  isRecurringEvent: z.string().optional(),
  recurringFrequency: z.string().optional(),
  
  // Additional details
  numberOfGuests: z.string().optional(),
  eventStyle: z.string().optional(),
  budget: z.string().optional(),
  budgetOther: z.string().optional(),
  tellUsMore: z.string().optional(),
  howDidYouHear: z.string().optional(),
  socialMediaPlatform: z.string().optional(),
  
  // General enquiry
  generalEnquiryType: z.string().optional(),
  generalEnquirySpecify: z.string().optional(),
  subject: z.string().optional(),
  hasSpecificDate: z.boolean().optional(),
  specificDate: z.string().nullable().optional(),
  
  // Privacy
  agreeToPrivacy: z.boolean()
});

// ────────────────────────────────────────────────────────────────────────────
// Debug endpoint — tests subitem column writes using SDK property names
// ────────────────────────────────────────────────────────────────────────────
export const debugSubitemColumns = createServerFn({ method: 'POST' })
  .validator((data: unknown) => z.object({ parentItemId: z.string() }).parse(data))
  .handler(async ({ data }) => {
    const board = new GeneralEnquiryBoard();
    const results: { step: string; success: boolean; detail: string }[] = [];

    // Test 1: Create subitem with just name
    let testSubitemId: string | null = null;
    try {
      const sub = await board.item(data.parentItemId)
        .subitem()
        .create({ name: 'DEBUG_SDK_TEST' })
        .execute();
      testSubitemId = sub?.id ?? null;
      results.push({ step: 'Create subitem (name only)', success: true, detail: `ID: ${testSubitemId}` });
    } catch (err: any) {
      results.push({ step: 'Create subitem (name only)', success: false, detail: err.message });
    }

    // Test 2: Create subitem with all columns inline (SDK property names from SCHEMA.md)
    try {
      const sub = await board.item(data.parentItemId)
        .subitem()
        .create({
          name: 'DEBUG_ALL_COLS',
          eventDate: new Date('2026-12-25'),
          day: 'Day 1 -',
          status: 'Working on it',
          venuePreferences: ['The Goods Shed'],
        } as any)
        .execute();
      results.push({ step: 'Create with all cols (eventDate/day/venuePreferences)', success: true, detail: `ID: ${sub?.id}` });
    } catch (err: any) {
      results.push({ step: 'Create with all cols (eventDate/day/venuePreferences)', success: false, detail: err.message });
    }

    // Test 3: Update individual fields on the first test subitem
    if (testSubitemId) {
      // Test eventDate
      try {
        await board.item(data.parentItemId).subitem(testSubitemId).update({ eventDate: new Date('2026-12-26') } as any).execute();
        results.push({ step: 'Update eventDate (Date object)', success: true, detail: 'OK' });
      } catch (err: any) {
        results.push({ step: 'Update eventDate (Date object)', success: false, detail: err.message });
      }

      // Test day
      try {
        await board.item(data.parentItemId).subitem(testSubitemId).update({ day: 'Day TEST -' } as any).execute();
        results.push({ step: 'Update day (string)', success: true, detail: 'OK' });
      } catch (err: any) {
        results.push({ step: 'Update day (string)', success: false, detail: err.message });
      }

      // Test venuePreferences
      try {
        await board.item(data.parentItemId).subitem(testSubitemId).update({ venuePreferences: ['The Goods Shed'] } as any).execute();
        results.push({ step: 'Update venuePreferences (string[])', success: true, detail: 'OK' });
      } catch (err: any) {
        results.push({ step: 'Update venuePreferences (string[])', success: false, detail: err.message });
      }

      // Test status
      try {
        await board.item(data.parentItemId).subitem(testSubitemId).update({ status: 'Working on it' } as any).execute();
        results.push({ step: 'Update status (string)', success: true, detail: 'OK' });
      } catch (err: any) {
        results.push({ step: 'Update status (string)', success: false, detail: err.message });
      }
    }

    console.log('=== SUBITEM DEBUG RESULTS ===');
    for (const r of results) {
      console.log(`  ${r.success ? '✅' : '❌'} ${r.step}: ${r.detail}`);
    }
    console.log('=== END DEBUG ===');

    return { results };
  });

// ────────────────────────────────────────────────────────────────────────────
// Main submission handler
// ────────────────────────────────────────────────────────────────────────────
export const submitEnquiry = createServerFn({ method: 'POST' })
  .validator((data: unknown) => enquirySchema.parse(data))
  .handler(async ({ data }) => {
    const board = new GeneralEnquiryBoard();
    
    const isEventEnquiry = data.enquiryType === 'Event Enquiry';

    // Staff-only enquiry types: only recognised members of our organisation may submit them
    const STAFF_ONLY_TYPES = ['Major Event', 'Internal'];
    if (isEventEnquiry && data.specificEnquiryType && STAFF_ONLY_TYPES.includes(data.specificEnquiryType)) {
      const viewer =
        data.staffUserId && data.staffAccountId
          ? await verifyStaffMember(data.staffUserId, data.staffAccountId).catch(() => null)
          : null;
      if (!viewer) {
        throw new Error('This enquiry type is only available to Atlantic Group staff.');
      }
    }
    
    const tellUsMore = data.tellUsMore || '';

    // STEP 0: Save to Vibe DB immediately as partial entry (captures all data even if board write fails)
    let vibeDocId: string | undefined;
    
    try {
      const user = await getCurrentUser();
      const partialRecord = {
        boardItemId: 'pending', // Will update after board creation
        name: isEventEnquiry ? data.fullName : data.name,
        email: data.email,
        contactNumber: data.contactNumber,
        suburb: data.suburb ?? undefined,
        state: data.state,
        enquiryType: data.enquiryType,
        specificEnquiryType: isEventEnquiry ? data.specificEnquiryType : data.generalEnquiryType,
        eventName: data.eventName,
        eventDateType: data.eventDateType,
        eventDate: data.eventDateType === 'one-day' && data.eventDate 
          ? data.eventDate 
          : data.eventDateType === 'multi-day' && data.eventDateRange?.start 
            ? data.eventDateRange.start 
            : undefined,
        venuePreferences: data.venuePreferences,
        numberOfGuests: data.numberOfGuests ? parseInt(data.numberOfGuests, 10) : undefined,
        eventStyle: data.eventStyle,
        budget: data.budget,
        weddingType: data.weddingType,
        corporateEventType: data.corporateEventType,
        businessName: data.businessName,
        industry: data.industry,
        companyWebsite: data.companyWebsite,
        socialEventType: data.socialEventType,
        externalCateringEventType: data.externalCateringEventType,
        externalVenue: data.externalVenue,
        isFlexibleWithDates: data.isFlexibleWithDates,
        isRecurringEvent: data.isRecurringEvent,
        recurringFrequency: data.recurringFrequency,
        howDidYouHear: data.howDidYouHear,
        socialMediaPlatform: data.socialMediaPlatform,
        tellUsMore: data.tellUsMore,
        agreeToPrivacy: data.agreeToPrivacy,
        submittedAt: new Date().toISOString(),
        createdBy: user?.id,
        status: 'Partial (pending board write)'
      };
      const vibeDoc = await db.collection(submissions).add(partialRecord);
      vibeDocId = vibeDoc.id;
      console.log('✅ Partial submission saved to Vibe DB, doc ID:', vibeDocId);
    } catch (vibeError) {
      console.error('❌ Failed to save partial submission to Vibe DB:', vibeError);
      // Continue anyway — board write might still succeed
    }

    // Step 1: Create item with basic safe fields only
    const basicItemData: any = {
      name: isEventEnquiry ? data.fullName : data.name,
      enquiryStatus: 'New Enquiry',
      creationDate: new Date(),
    };

    if (data.email) basicItemData.email = { email: data.email, label: data.email };
    if (data.contactNumber) basicItemData.contactNumber = { phone: data.contactNumber.replace(/\s/g, ''), country: 'AU' };
    if (data.state) basicItemData.state = data.state;
    if (isEventEnquiry && data.specificEnquiryType) basicItemData.enquiryType = data.specificEnquiryType;
    if (!isEventEnquiry && data.generalEnquiryType) basicItemData.enquiryType = data.generalEnquiryType;
    if (tellUsMore) basicItemData.tellUsMore = tellUsMore;
    if (typeof data.agreeToPrivacy === 'boolean') basicItemData.agreeToPrivacyDisclaimer = data.agreeToPrivacy;

    // Suburb → location column (requires lat/lng/address)
    if (data.suburb && typeof data.suburb === 'object' && 'lat' in data.suburb && 'lng' in data.suburb) {
      basicItemData.suburb = {
        lat: String(data.suburb.lat),
        lng: String(data.suburb.lng),
        address: data.suburb.address
      };
    }

    console.log('Creating item with basic fields:', JSON.stringify(basicItemData, null, 2));

    const createdItem = await board.item()
      .create(basicItemData)
      .inGroup('topics')
      .returnColumns(['enquiryStatus', 'eventName'])
      .execute();

    console.log('Item created successfully:', createdItem.id);
    
    // Update Vibe DB with board item ID
    if (vibeDocId) {
      try {
        await db.collection(submissions).doc(vibeDocId).update({
          boardItemId: createdItem.id,
          status: 'New Enquiry'
        });
        console.log('✅ Vibe DB record updated with board item ID');
      } catch (updateError) {
        console.error('⚠️ Failed to update Vibe DB with board item ID (non-fatal):', updateError);
      }
    }

    // Step 2: Update item with event-specific fields
    const updateData: any = {};

    if (isEventEnquiry) {
      if (data.eventName) updateData.eventName = data.eventName;
      if (data.pleaseSpecify) updateData.pleaseSpecify = data.pleaseSpecify;
      if (data.weddingType) updateData.weddingType = data.weddingType;
      if (data.venuePreferences && data.venuePreferences.length > 0) {
        updateData.venuePreferences = data.venuePreferences;
      }
      if (data.venuePreferences?.includes('Other') && data.venueIfOther) {
        updateData.venueIfOther = data.venueIfOther;
      }
      if (data.socialEventType) updateData.socialEventType = data.socialEventType;
      if (data.corporateEventType) updateData.corporateEventType = data.corporateEventType;
      if (data.businessName) updateData.businessNameCompany = data.businessName;
      if (data.industry) updateData.industry = data.industry;
      if (data.externalCateringEventType) updateData.externalCateringEventType = data.externalCateringEventType;
      if (data.externalVenue) updateData.externalVenue = data.externalVenue;
      if (data.numberOfGuests) updateData.numberOfGuests = parseInt(data.numberOfGuests, 10);
      if (data.eventStyle) updateData.eventStyle = data.eventStyle;
      if (data.budget) updateData.budget = data.budget;
      if (data.budgetOther) updateData.budgetIfOther = parseFloat(data.budgetOther);
      
      if (data.eventDateType === 'one-day' && data.eventDate) {
        updateData.eventDate = new Date(data.eventDate);
      } else if (data.eventDateType === 'multi-day' && data.eventDateRange?.start) {
        updateData.eventDate = new Date(data.eventDateRange.start);
      }
      
      if (data.eventDateType === 'multi-day' || data.eventDateType === 'one-day') {
        updateData.eventDateType = data.eventDateType === 'multi-day' ? 'Multi-day event' : 'One-day event';
      }
      
      if (data.isFlexibleWithDates === 'yes') updateData.flexibleDate = 'Yes';
      else if (data.isFlexibleWithDates === 'no') updateData.flexibleDate = 'No';
      
      if (data.isRecurringEvent === 'yes' || data.isRecurringEvent === 'no') {
        updateData.recurring = data.isRecurringEvent === 'yes' ? 'Yes' : 'No';
      }
      if (data.isRecurringEvent === 'yes' && data.recurringFrequency) {
        updateData.frequency = data.recurringFrequency;
      }
    } else {
      if (data.generalEnquirySpecify) updateData.pleaseSpecifyIfOther = data.generalEnquirySpecify;
    }
    
    if (data.howDidYouHear) updateData.howDidYouHearAboutUs = data.howDidYouHear;
    if (data.socialMediaPlatform) updateData.whichSocialMediaPlatformDidYouSeeUsOn = data.socialMediaPlatform;

    if (Object.keys(updateData).length > 0) {
      try {
        console.log('Updating item with event fields:', JSON.stringify(updateData, null, 2));
        await board.item(createdItem.id).update(updateData).execute();
        console.log('Event fields updated successfully');
      } catch (updateError) {
        console.error('Batch update failed, trying one-by-one:', updateError);
        for (const [key, value] of Object.entries(updateData)) {
          try {
            await board.item(createdItem.id).update({ [key]: value } as any).execute();
            console.log(`Field "${key}" updated OK`);
          } catch (fieldError) {
            console.error(`Field "${key}" FAILED with value:`, JSON.stringify(value), fieldError);
          }
        }
      }
    }

    // Website link (separate — link columns are finicky)
    if (isEventEnquiry && data.companyWebsite) {
      const websiteUrl = data.companyWebsite.startsWith('http') ? data.companyWebsite : `https://${data.companyWebsite}`;
      try {
        await board.item(createdItem.id).update({
          website: { url: websiteUrl, label: websiteUrl }
        } as any).execute();
        console.log('Website updated OK');
      } catch (linkError) {
        console.error('Website link update failed:', linkError);
      }
    }


    // ────────────────────────────────────────────────────────────────
    // SUBITEMS — use raw GraphQL create_subitem with column_values
    // ────────────────────────────────────────────────────────────────
    let mainDates: Date[] = [];
    let flexibleDates: Date[] = [];
    
    if (isEventEnquiry) {
      if (data.eventDateType === 'one-day' && data.eventDate) {
        mainDates = [new Date(data.eventDate)];
      } else if (data.eventDateType === 'multi-day' && data.eventDateRange?.start && data.eventDateRange?.end) {
        const start = new Date(data.eventDateRange.start);
        const end = new Date(data.eventDateRange.end);
        const current = new Date(start);
        while (current <= end) {
          mainDates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
      }
      
      if (data.isFlexibleWithDates === 'yes') {
        if (data.eventDateType === 'one-day' && data.flexibleDates && data.flexibleDates.length > 0) {
          flexibleDates = data.flexibleDates.map(d => new Date(d));
        } else if (data.eventDateType === 'multi-day' && data.flexibleDateRange?.start && data.flexibleDateRange?.end) {
          const start = new Date(data.flexibleDateRange.start);
          const end = new Date(data.flexibleDateRange.end);
          const current = new Date(start);
          while (current <= end) {
            flexibleDates.push(new Date(current));
            current.setDate(current.getDate() + 1);
          }
        }
      }
    } else if (data.hasSpecificDate && data.specificDate) {
      mainDates = [new Date(data.specificDate)];
    }
    
    const venuePrefs = data.venuePreferences || [];
    
    console.log('=== SUBITEM CREATION START ===');
    console.log(`Parent item: ${createdItem.id}`);
    console.log(`Main dates: ${mainDates.length}, Flexible dates: ${flexibleDates.length}`);
    console.log(`Venue prefs: ${JSON.stringify(venuePrefs)}`);

    // Subitem SDK property names (from SCHEMA.md):
    //   eventDate (date0) — date column
    //   day (text_mm6td5tr) — text column
    //   venuePreferences (dropdown_mm6t9p84) — dropdown column
    //   status — status column

    // Create main date subitems
    for (let i = 0; i < mainDates.length; i++) {
      const date = mainDates[i];
      const dayNum = i + 1;
      
      let subitemName = `Day ${dayNum}`;
      if (venuePrefs.length > 0) {
        subitemName += ` - ${venuePrefs.join(', ')}`;
      }
      
      const createPayload: any = {
        name: subitemName,
        eventDate: date,
        day: `Day ${dayNum} -`,
        status: 'Working on it',
      };
      if (venuePrefs.length > 0) {
        createPayload.venuePreferences = venuePrefs;
      }

      try {
        console.log(`[Main ${dayNum}] Creating subitem with payload:`, JSON.stringify(createPayload, null, 2));
        const sub = await board.item(createdItem.id)
          .subitem()
          .create(createPayload)
          .execute();
        console.log(`[Main ${dayNum}] ✅ Created subitem ${sub?.id}`);
      } catch (err: any) {
        console.error(`[Main ${dayNum}] ❌ Full create failed:`, err.message);
        
        // Fallback: create with name only, then update each field individually
        try {
          const fallback = await board.item(createdItem.id)
            .subitem()
            .create({ name: subitemName })
            .execute();
          console.log(`[Main ${dayNum}] Fallback created: ${fallback?.id}. Updating fields...`);
          
          if (fallback?.id) {
            const fields: [string, unknown][] = [
              ['eventDate', date],
              ['day', `Day ${dayNum} -`],
              ['status', 'Working on it'],
            ];
            if (venuePrefs.length > 0) fields.push(['venuePreferences', venuePrefs]);
            
            for (const [key, val] of fields) {
              try {
                await board.item(createdItem.id).subitem(fallback.id).update({ [key]: val } as any).execute();
                console.log(`[Main ${dayNum}]   ✅ "${key}" OK`);
              } catch (fieldErr: any) {
                console.error(`[Main ${dayNum}]   ❌ "${key}" FAILED:`, JSON.stringify(val), fieldErr.message);
              }
            }
          }
        } catch (fallbackErr: any) {
          console.error(`[Main ${dayNum}] ❌ Even fallback create failed:`, fallbackErr.message);
        }
      }
    }
    
    // Create flexible date subitems
    for (let i = 0; i < flexibleDates.length; i++) {
      const date = flexibleDates[i];
      
      let subitemName = 'FLEX -';
      if (venuePrefs.length > 0) {
        subitemName += ` ${venuePrefs.join(', ')}`;
      }
      
      const createPayload: any = {
        name: subitemName,
        eventDate: date,
        day: 'FLEX -',
        status: 'Working on it',
      };
      if (venuePrefs.length > 0) {
        createPayload.venuePreferences = venuePrefs;
      }
      
      try {
        console.log(`[Flex ${i + 1}] Creating subitem with payload:`, JSON.stringify(createPayload, null, 2));
        const sub = await board.item(createdItem.id)
          .subitem()
          .create(createPayload)
          .execute();
        console.log(`[Flex ${i + 1}] ✅ Created subitem ${sub?.id}`);
      } catch (err: any) {
        console.error(`[Flex ${i + 1}] ❌ Full create failed:`, err.message);
        
        try {
          const fallback = await board.item(createdItem.id)
            .subitem()
            .create({ name: subitemName })
            .execute();
          console.log(`[Flex ${i + 1}] Fallback created: ${fallback?.id}. Updating fields...`);
          
          if (fallback?.id) {
            const fields: [string, unknown][] = [
              ['eventDate', date],
              ['day', 'FLEX -'],
              ['status', 'Working on it'],
            ];
            if (venuePrefs.length > 0) fields.push(['venuePreferences', venuePrefs]);
            
            for (const [key, val] of fields) {
              try {
                await board.item(createdItem.id).subitem(fallback.id).update({ [key]: val } as any).execute();
                console.log(`[Flex ${i + 1}]   ✅ "${key}" OK`);
              } catch (fieldErr: any) {
                console.error(`[Flex ${i + 1}]   ❌ "${key}" FAILED:`, JSON.stringify(val), fieldErr.message);
              }
            }
          }
        } catch (fallbackErr: any) {
          console.error(`[Flex ${i + 1}] ❌ Even fallback create failed:`, fallbackErr.message);
        }
      }
    }
    
    const totalDateCount = mainDates.length + flexibleDates.length;
    console.log('=== SUBITEM CREATION END ===');
    console.log(`Submission complete! Item ${createdItem.id}, ${totalDateCount} subitems`);
    
    return {
      success: true,
      itemId: createdItem.id,
      itemName: createdItem.name,
      dateCount: totalDateCount
    };
  });
